import { Injectable, signal } from '@angular/core';
import { Toast, ToastAction, ToastType } from 'src/ui/types/toast.type';

const DEFAULT_DURATION_MS = 4000;

export interface ToastOptions {
  type?: ToastType;
  position?: 'left' | 'right';
  duration?: number;
  action?: ToastAction;
}

@Injectable({ providedIn: 'root' })
export class ToastService {
  private idCounter = 0;

  readonly toasts = signal<Toast[]>([]);

  show(message: string, options: ToastOptions = {}): number {
    const id = ++this.idCounter;
    const { type = 'info', position = 'right', duration = DEFAULT_DURATION_MS, action } = options;

    this.toasts.update((list) => [...list, { id, message, type, position, action }]);
    if (duration > 0) setTimeout(() => this.remove(id), duration);

    return id;
  }

  success(message: string, options?: Omit<ToastOptions, 'type'>): number {
    return this.show(message, { ...options, type: 'success' });
  }

  warning(message: string, options?: Omit<ToastOptions, 'type'>): number {
    return this.show(message, { ...options, type: 'warning' });
  }

  danger(message: string, options?: Omit<ToastOptions, 'type'>): number {
    return this.show(message, { ...options, type: 'danger' });
  }

  info(message: string, options?: Omit<ToastOptions, 'type'>): number {
    return this.show(message, { ...options, type: 'info' });
  }

  remove(id: number): void {
    this.toasts.update((list) => list.filter((toast) => toast.id !== id));
  }

  clearAll(): void {
    this.toasts.set([]);
  }
}
